import {USER_FIELD_TYPES} from 'helpers/constants'
import {find, get, isEmpty} from 'lodash'
import {filterDormantContacts, getFixedCompanyGroups} from 'helpers/contacts'
import {getBespokePrintTemplates, getStandardPrintTemplates} from 'helpers/printTemplates'
import {getUser} from 'selectors/auth'

// we need to return the minimum date
export const getNextBillingDateSelector = (state) => {
  const accountPackages = state.data.accountPackages
  return isEmpty(accountPackages)
      ? null
      : accountPackages.reduce((cur, ap) =>
          (ap.expiryDate > cur && cur !== 0)
              ? cur
              : ap.expiryDate, 0)
}

export const getPackages = state => state.data.packages || []

export const getAccountPackages = state => state.data.accountPackages || []

export const getCancellationDateSelector = (state) => {
  const user = getUser(state)
  return get(user, 'cancellingDate')
}

export const getActivityReasons = state => state.data.activityReasons || []

export const getContactMethods = state => state.data.contactMethods || []

export const getDepartments = state => (find(state.data.contactFields, {field: 'departments'}) || {}).values

export const getHomeCurrency = state => state.data.currencies.find(currency => currency.default === true)

export const getCurrencies = state => state.data.currencies || []

export const getBusinessTypes = state => state.data.businessTypes

export const getDefaultCurrencies = state => state.data.defaultCurrencies || []

export const getGlobalCurrencies = state => state.settings.globalCurrencies || []

export const getNominalCodes = state => {
  const nominalCodes = state.data.nominalCodes || []
  return nominalCodes.filter(nominalCode => !isEmpty(nominalCode.code))
}

export const getTaxCodes = state => state.data.taxCodes || []

export const getDefaultTaxCode = state => {
  return getTaxCodes(state).find(taxCode => taxCode.isDefault)
}

export const getHomeContacts = (state, dormants = false) => {
  const homeContacts = get(state, 'data.homeContacts', [])
  return filterDormantContacts(homeContacts, dormants)
}

export const getTeams = state => state.data.teams || []

export const getTeamMembers = state => state.data.teamMembers || []

export const getPartCategories = state => state.data.partCategories || []

export const getLocations = state => state.data.locations || []

export const getFieldOptions = state => state.data.fieldOptions || []

export const getProductionReasons = state => state.data.productionReasons.data || []

export const getSendAddresses = state => state.data.sendAddresses || []

export const getDeliveryMethods = state => state.data.deliveryMethods.data || []

export const getDeliveryReturnReasons = state => state.data.deliveryReturnReasons.data || []

export const getOrderTypes = state => state.data.orderTypes || []

export const getOrderReasons = state => state.data.orderReasons || []

export const isJobTypeMandatory = state => state.auth.user.settings.opportunityCreateRequiredFields.required.OPPORTUNITY_CREATE_JOB_TYPE || false

export const getTextTemplates = state => state.data.textTemplates || []

export const getUserFields = (state, userFieldType) => {
  switch (userFieldType) {
    case USER_FIELD_TYPES.CONTACT_USER_FIELD:
      return state.data.contactFields || []
    case USER_FIELD_TYPES.COMPANY_USER_FIELD:
      return state.data.companyFields || []
    case USER_FIELD_TYPES.QUOTE_USER_FIELD:
      return state.data.quotationFields || []
    case USER_FIELD_TYPES.QUOTE_ITEM_USER_FIELD:
      return state.data.quotationItemFields || []
    case USER_FIELD_TYPES.PURCHASE_ORDER_USER_FIELD:
      return state.data.purchasingFields || []
    case USER_FIELD_TYPES.PURCHASE_ORDER_ITEM_USER_FIELD:
      return state.data.purchasingItemFields || []
  }
}

export const getCountries = state => state.data.countries || []
export const getLocationTypes = state => state.data.locationTypes
export const getReferralTypes = state => state.data.referralTypes

export const getPrintTemplates = state => {
  const printTemplates = state.data.printTemplates || []

  const standardTemplates = getStandardPrintTemplates(printTemplates)

  const bespokeTemplates = getBespokePrintTemplates(printTemplates)

  return [
    ...standardTemplates,
    ...bespokeTemplates,
  ]
}

export const getPrintTemplateGroups = state => state.data.printTemplateGroups || []

export const getCampaigns = state => state.data.campaigns || []

export const getCompanyGroups = state => {
  const companyGroups = state.data.companyGroups || []
  return [
    ...getFixedCompanyGroups(),
    ...companyGroups,
  ]
}

export const getTrialPeriod = state => state.data.trialPeriod

export const getMinPasswordLength = state => state.data.minPasswordLength

export const getQuickBooksBaseURL = state => state.data.quickBooksBaseURL || ''

export const getPlansURL = state => state.data.plansUrl

export const getSupportPhone = state => state.data.supportPhone

export const getPaymentDetails = state => state.data.paymentDetails

export const getOpportunityStages = state => state.data.opportunityStages.data || []

export const getHomeCompanyLocations = state => state.data.homeCompanyLocations || []

export const getSessionId = state => state.data.sessionId

export const getLayouts = state => state.data.layouts || []

export const getGlobalLayouts = state => state.data.globalLayouts || []

export const getProducts = state => state.data.products || []

export const getBillingDetails = state => get(state, 'data.billingDetails', null)

export const getBillingInvoices = state => get(state, 'data.billingInvoices', [])

export const getHasMoreBillingInvoices = state => get(state, 'hasMoreBillingInvoices', false)

export const getServiceHealth = state => get(state, 'data.serviceHealth', {})

export const getSystemName = state => get(state, 'data.systemName', '')

export const getAccountingDepartments = state => state.data.accountingDepartments

export const getRecaptchaSiteKey = state => state.data.recaptchaSiteKey